import React, {useState, useEffect, useRef} from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { customerSlice } from "../../store";
import {
  AuthService,
  ResourceService,
  CustomerService,
  getAnniversaryMonthFromAdmissionDate,
  formatDateUsMdY,
  computeNextAdcapsDueDate,
  computeAdcapsRemainingDays,
  emptyAdmissionRecordRow,
  emptyPriorAdmissionRecordRow,
  sanitizeAdmissionRecordsForApi,
  legacyAdmissionFieldsFromFirstRecord,
} from "../../services";
import Select from 'react-select';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { 
  CUSTOMER_TYPE, CUSTOMER_TYPE_TEXT, 
  PROGRAM_TYPE, PROGRAM_TYPE_TEXT,
  PAY_SOURCE, PAY_SOURCE_TEXT,
  LEGAL_SEX, LEGAL_SEX_TEXT,
  MARITAL_STATUS, MARITAL_STATUS_TEXT,
  IMMIGRATION_STATUS, IMMIGRATION_STATUS_TEXT,
  LANGUAGE_OPTIONS,
  STATE_OPTIONS, STATE_OPTIONS_TEXT,
  EMERGENCY_CONTACT_RELATIONSHIP, EMERGENCY_CONTACT_RELATIONSHIP_TEXT,
  EMERGENCY_CONTACT_ROLE_OPTIONS,
  DAYS_OF_WEEK_OPTIONS,
  REFERRAL_SOURCE_TEXT,
  REFERRAL_SOURCE_GROUPED,
  ADMITTED_FROM_TEXT,
  ADMITTED_FROM_GROUPED,
  CUSTOMER_DISCHARGE_REASON_TEXT,
  CUSTOMER_DISCHARGE_REASON_GROUPED,
  DIETARY_RESTRICTIONS_OPTIONS,
  DIETARY_RESTRICTIONS_GROUPED,
  DIET_TEXTURE, DIET_TEXTURE_TEXT,
  TRANSPORTATION_TYPE, TRANSPORTATION_TYPE_TEXT,
  YES_NO, YES_NO_TEXT,
  PRE_SCREENING_FORM, PRE_SCREENING_FORM_TEXT,
  PREFERRED_TEXT_LANGUAGE, PREFERRED_TEXT_LANGUAGE_TEXT,
  MultiSelectDropdown,
  MEAL_TYPE,
  MEAL_TYPE_TEXT,
  SOCIAL_BENEFIT_FIELD_KEYS,
  useUnsavedChangesGuard,
  useFormDirtyTracker,
  emptyCustomerAddress,
  getCustomerAddressBySlot,
  findNextCustomerAddressSlot,
  sortCustomerAddressesBySlot,
  buildCustomerAddressSaveFields,
  MAX_CUSTOMER_ADDRESS_SLOTS,
  isPcpSpecialty,
  isActiveProvider,
} from "../../shared";
import { findDuplicateCustomer } from "../../shared/utils/duplicateChecks";
import { Spinner, Breadcrumb, BreadcrumbItem, Tabs, Tab, Modal, Button } from "react-bootstrap";
import { Upload } from "react-bootstrap-icons";

const MAX_ADMISSION_RECORDS = 5;

const flattenSelectOptions = (optionsList) => {
  if (!optionsList || !Array.isArray(optionsList)) return [];
  return optionsList.reduce((acc, opt) => {
    if (opt && Array.isArray(opt.options)) return [...acc, ...opt.options];
    if (opt) return [...acc, opt];
    return acc;
  }, []);
};

const withGroupedOrphanOption = (grouped, rawValue, textMap = {}) => {
  if (rawValue === undefined || rawValue === null || rawValue === '') return grouped;
  const flat = flattenSelectOptions(grouped);
  const rs = String(rawValue);
  if (flat.some((o) => o && String(o.value) === rs)) return grouped;
  const label = textMap[rawValue] ?? textMap[rs] ?? rs;
  return [...(grouped || []), { label: '—', options: [{ value: rawValue, label: String(label) }] }];
};

const CreateCustomer = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { createCustomer } = customerSlice.actions;
  const [duplicateCustomerModalOpen, setDuplicateCustomerModalOpen] = useState(false);
  const pendingCustomerPayloadRef = useRef(null);
  
  // Basic Info
  const [firstname, setFirstname] = useState('');
  const [middleName, setMiddleName] = useState('');
  const [lastname, setLastname] = useState('');
  const [nameCN, setNameCN] = useState('');
  const [nameOnId, setNameOnId] = useState('');
  const [customerType, setCustomerType] = useState('');
  const [programType, setProgramType] = useState('');
  const [paySource, setPaySource] = useState('');
  const [paySourceOther, setPaySourceOther] = useState('');
  const [birthDate, setBirthDate] = useState(null);
  const [legalSex, setLegalSex] = useState('');
  const [maritalStatus, setMaritalStatus] = useState('');
  const [marriageDate, setMarriageDate] = useState(null);
  const [spouseName, setSpouseName] = useState('');
  const [spouseCustomerId, setSpouseCustomerId] = useState('');
  const [spouseCustomerOptions, setSpouseCustomerOptions] = useState([]);
  const [immigrationStatus, setImmigrationStatus] = useState('');
  const [immigrationStatusOther, setImmigrationStatusOther] = useState('');
  const [languageSpoken, setLanguageSpoken] = useState([]);
  const [languageSpokenOther, setLanguageSpokenOther] = useState('');
  
  // Contact Info
  const [phone, setPhone] = useState('');
  const [phone2, setPhone2] = useState('');
  const [phone3, setPhone3] = useState('');
  const [email, setEmail] = useState('');
  
  // Addresses (stable slots 1–5; deleting a middle slot does not renumber later ones)
  const [addresses, setAddresses] = useState([
    emptyCustomerAddress(1)
  ]);
  
  // Emergency Contacts (up to 2)
  const [emergencyContacts, setEmergencyContacts] = useState([
    { name: '', phone: '', relationship: '', relationshipOther: '', role: [] }
  ]);
  
  // Schedule
  const [daysOfWeek, setDaysOfWeek] = useState([]);
  
  // Admission & Discharge Record
  const [admissionRecords, setAdmissionRecords] = useState([emptyAdmissionRecordRow()]);
  const [dischargeDate, setDischargeDate] = useState(null);
  const [dischargeReason, setDischargeReason] = useState('');
  const [dischargeReasonOther, setDischargeReasonOther] = useState('');
  
  // Care & Services
  const [dietaryRestrictions, setDietaryRestrictions] = useState([]);
  const [dietaryRestrictionsOther, setDietaryRestrictionsOther] = useState('');
  const [dietTexture, setDietTexture] = useState('');
  const [mealType, setMealType] = useState('');
  const [foodAllergies, setFoodAllergies] = useState('');
  const [tableId, setTableId] = useState('');
  const [seatNumber, setSeatNumber] = useState('');
  const [transportationType, setTransportationType] = useState('');
  const [consentToTextMessages, setConsentToTextMessages] = useState('');
  const [preferredTextLanguage, setPreferredTextLanguage] = useState('');
  const [consentToMediaUse, setConsentToMediaUse] = useState('');
  const selectableCustomerTypes = [CUSTOMER_TYPE.MEMBER, CUSTOMER_TYPE.VISITOR];
  
  // Medical & Insurance - Providers
  const [primaryCarePhysician, setPrimaryCarePhysician] = useState('');
  const [pharmacy, setPharmacy] = useState('');
  const [pharmacyId, setPharmacyId] = useState('');
  const [providers, setProviders] = useState([]);
  const pcpProviders = providers.filter((provider) => isActiveProvider(provider) && isPcpSpecialty(provider));
  const [pharmacies, setPharmacies] = useState([]);
  const activePharmacies = pharmacies.filter(isActiveProvider);
  
  // General Conditions
  const [diabetesMellitus, setDiabetesMellitus] = useState('');
  const [eyesOn, setEyesOn] = useState('');
  const [eyesOnReason, setEyesOnReason] = useState('');
  const [hasAirtag, setHasAirtag] = useState('');
  const [weight, setWeight] = useState('');
  const [wheelchair, setWheelchair] = useState('');
  
  // Legal
  const [molst, setMolst] = useState('');
  const [provisionsForAdvanceMedical, setProvisionsForAdvanceMedical] = useState('');
  const [hospice, setHospice] = useState('');
  const [burialArrangements, setBurialArrangements] = useState('');
  const [powerOfAttorney, setPowerOfAttorney] = useState('');
  const [adcapsNote, setAdcapsNote] = useState('');

  // Medication Management
  const [medicationsGivenAtCenter, setMedicationsGivenAtCenter] = useState('');
  const [pillbox, setPillbox] = useState('');
  const [medicationNote, setMedicationNote] = useState('');
  const [medicationType, setMedicationType] = useState('');
  const [pillboxType, setPillboxType] = useState('');
  const [pillboxNote, setPillboxNote] = useState('');
  
  // Rounding
  const [requiresRounding, setRequiresRounding] = useState('');
  const [roundingNotes, setRoundingNotes] = useState('');
  
  // Confidential Details
  const [medicareNumber, setMedicareNumber] = useState('');
  const [medicaidNumber, setMedicaidNumber] = useState('');
  const [socialSecurityNumber, setSocialSecurityNumber] = useState('');
  const [socialBenefits, setSocialBenefits] = useState(() =>
    Object.fromEntries(SOCIAL_BENEFIT_FIELD_KEYS.map(({ key }) => [key, '']))
  );
  const [ltssIdNumber, setLtssIdNumber] = useState('');
  
  // Compliance & Deadlines
  const [adcapsCompletedDate, setAdcapsCompletedDate] = useState(null);
  const [medicaidExpirationDate, setMedicaidExpirationDate] = useState(null);
  const [vaExpirationDate, setVaExpirationDate] = useState(null);
  const [medicaidRenewal, setMedicaidRenewal] = useState('');
  const [medicaidRenewDate, setMedicaidRenewDate] = useState(null);
  const [idExpirationDate, setIdExpirationDate] = useState(null);
  
  // Form Submission
  const [hipaaAuthorizationForm, setHipaaAuthorizationForm] = useState(null);
  const [medicationManagementConsentForm, setMedicationManagementConsentForm] = useState(null);
  const [freedomOfChoiceForm, setFreedomOfChoiceForm] = useState(null);
  const [mealBenefitApplicationForm, setMealBenefitApplicationForm] = useState(null);
  const [photoVideoReleaseForm, setPhotoVideoReleaseForm] = useState(null);
  const [securityDepositAgreementForm, setSecurityDepositAgreementForm] = useState(null);
  const [recreationalProgramContractForm, setRecreationalProgramContractForm] = useState(null);
  const [tbForm, setTbForm] = useState(null);
  const [preScreeningForm, setPreScreeningForm] = useState(null);
  const [tbScreeningYesNo, setTbScreeningYesNo] = useState('');
  const [preScreeningYesNo, setPreScreeningYesNo] = useState('');
  const [fortyFiveDayAdmissionNote, setFortyFiveDayAdmissionNote] = useState('');
  const [tbNotes, setTbNotes] = useState('');
  
  // Additional Information
  const [note, setNote] = useState('');
  const [selectedFile, setSelectedFile] = useState();

  const { isDirty, resetBaseline } = useFormDirtyTracker({
    firstname, middleName, lastname, nameCN, nameOnId, customerType, programType,
    paySource, paySourceOther, birthDate, legalSex, maritalStatus, marriageDate,
    spouseName, spouseCustomerId, immigrationStatus, immigrationStatusOther,
    languageSpoken, languageSpokenOther, phone, phone2, phone3, email,
    addresses, emergencyContacts, daysOfWeek, admissionRecords, dischargeDate,
    dischargeReason, dischargeReasonOther, dietaryRestrictions, dietaryRestrictionsOther,
    dietTexture, mealType, foodAllergies, tableId, seatNumber, transportationType,
    consentToTextMessages, preferredTextLanguage, consentToMediaUse,
    primaryCarePhysician: primaryCarePhysician?.value ?? '',
    pharmacy: pharmacy?.value ?? '', pharmacyId,
    diabetesMellitus, eyesOn, eyesOnReason, hasAirtag, weight, wheelchair,
    molst, provisionsForAdvanceMedical, hospice, burialArrangements, powerOfAttorney, adcapsNote,
    medicationsGivenAtCenter, pillbox, medicationNote, medicationType, pillboxType, pillboxNote, requiresRounding, roundingNotes,
    medicareNumber, medicaidNumber, socialSecurityNumber, socialBenefits, ltssIdNumber,
    adcapsCompletedDate, medicaidExpirationDate, vaExpirationDate, medicaidRenewal,
    medicaidRenewDate, idExpirationDate,
    hipaaAuthorizationForm: hipaaAuthorizationForm ? `${hipaaAuthorizationForm.name}|${hipaaAuthorizationForm.size}` : '',
    medicationManagementConsentForm: medicationManagementConsentForm ? `${medicationManagementConsentForm.name}|${medicationManagementConsentForm.size}` : '',
    freedomOfChoiceForm: freedomOfChoiceForm ? `${freedomOfChoiceForm.name}|${freedomOfChoiceForm.size}` : '',
    mealBenefitApplicationForm: mealBenefitApplicationForm ? `${mealBenefitApplicationForm.name}|${mealBenefitApplicationForm.size}` : '',
    photoVideoReleaseForm: photoVideoReleaseForm ? `${photoVideoReleaseForm.name}|${photoVideoReleaseForm.size}` : '',
    securityDepositAgreementForm: securityDepositAgreementForm ? `${securityDepositAgreementForm.name}|${securityDepositAgreementForm.size}` : '',
    recreationalProgramContractForm: recreationalProgramContractForm ? `${recreationalProgramContractForm.name}|${recreationalProgramContractForm.size}` : '',
    tbForm: tbForm ? `${tbForm.name}|${tbForm.size}` : '',
    preScreeningForm: preScreeningForm ? `${preScreeningForm.name}|${preScreeningForm.size}` : '',
    tbScreeningYesNo, preScreeningYesNo, fortyFiveDayAdmissionNote, tbNotes, note,
    selectedFile: selectedFile ? `${selectedFile.name}|${selectedFile.size}` : '',
  });
  const { tryNavigate, tryGoBack } = useUnsavedChangesGuard(isDirty);

  const goBack = () => {
    tryGoBack('/customers/list');
  };

  const redirectAfterSave = () => {
    tryNavigate('/customers/list');
  };

  // Phone number formatting function
  const getCustomerDisplayNameForSpouse = (c) => {
    if (!c) return '';
    const first = `${c.firstname || ''}`.trim();
    const last = `${c.lastname || ''}`.trim();
    if (first || last) return [first, last].filter(Boolean).join(' ');
    const legacy = `${c.name || ''}`.trim();
    return legacy || 'Customer';
  };

  const handleSpouseCustomerSelect = (opt) => {
    if (!opt) {
      setSpouseCustomerId('');
      return;
    }
    setSpouseCustomerId(String(opt.value));
    setSpouseName(opt.label);
  };

  const handleSpouseNameChange = (e) => {
    setSpouseName(e.target.value);
    setSpouseCustomerId('');
  };

  const formatPhoneNumber = (value) => {
    if (!value) return '';
    // Remove all non-numeric characters
    const phoneNumber = value.replace(/[^\d]/g, '');
    const phoneNumberLength = phoneNumber.length;
    
    if (phoneNumberLength < 4) {
      return phoneNumber;
    }
    if (phoneNumberLength < 7) {
      return `(${phoneNumber.slice(0, 3)})${phoneNumber.slice(3)}`;
    }
    return `(${phoneNumber.slice(0, 3)})${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
  };

  useEffect(() => {
    if (!AuthService.canAddOrEditCustomers()) {
      window.alert('You haven\'t login yet OR this user does not have access to this page. Please change an admin account to login.')
      AuthService.logout();
      navigate(`/login`);
    }
    ResourceService.getAll('pharmacy').then(data => {
      setPharmacies(data.data);
    });
    ResourceService.getAll().then((data) => {
      const sortedProviders = [...(data?.data || [])].sort((a, b) =>
        `${a?.name || ''}`.localeCompare(`${b?.name || ''}`)
      );
      setProviders(sortedProviders);
    });
    CustomerService.getAllActiveCustomers().then((res) => {
      const list = res.data || [];
      const opts = list
        .map((c) => {
          const id = `${c._id != null ? c._id : c.id || ''}`;
          if (!id) return null;
          return { value: id, label: getCustomerDisplayNameForSpouse(c) };
        })
        .filter(Boolean)
        .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
      setSpouseCustomerOptions(opts);
    }).catch(() => setSpouseCustomerOptions([]));
  }, [])

  const formatDateForBackend = (date) => {
    if (!date) return '';
    const d = new Date(date);
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const year = d.getFullYear();
    return `${month}/${day}/${year}`;
  }

  const onPharmacyChange = (selectedPharmacy) => {
    setPharmacy(selectedPharmacy);
    setPharmacyId(selectedPharmacy?.value);
  }

  const onPrimaryCarePhysicianChange = (selectedProvider) => {
    setPrimaryCarePhysician(selectedProvider);
  }

  // Address management functions (stable slots — gaps stay empty until reused)
  const addAddress = () => {
    const nextSlot = findNextCustomerAddressSlot(addresses);
    if (nextSlot == null) return;
    setAddresses(sortCustomerAddressesBySlot([...addresses, emptyCustomerAddress(nextSlot)]));
  };

  const removeAddress = (slot) => {
    if (Number(slot) === 1) return; // Address 1 is required
    if (addresses.length <= 1) return;
    setAddresses(addresses.filter((address) => Number(address.slot) !== Number(slot)));
  };

  const updateAddress = (slot, field, value) => {
    setAddresses((prev) =>
      prev.map((address) =>
        Number(address.slot) === Number(slot) ? { ...address, [field]: value } : address
      )
    );
  };

  // Emergency contact management functions
  const addEmergencyContact = () => {
    if (emergencyContacts.length < 5) {
      setEmergencyContacts([...emergencyContacts, { name: '', phone: '', relationship: '', relationshipOther: '', role: [] }]);
    }
  };

  const removeEmergencyContact = (index) => {
    if (emergencyContacts.length > 1) {
      setEmergencyContacts(emergencyContacts.filter((_, i) => i !== index));
    }
  };

  const updateEmergencyContact = (index, field, value) => {
    const newContacts = [...emergencyContacts];
    newContacts[index][field] = value;
    setEmergencyContacts(newContacts);
  };

  const addAdmissionRecord = () => {
    if (admissionRecords.length < MAX_ADMISSION_RECORDS) {
      setAdmissionRecords([...admissionRecords, emptyPriorAdmissionRecordRow()]);
    }
  };

  const removeAdmissionRecord = (index) => {
    if (index === 0) return;
    if (admissionRecords.length <= 1) return;
    setAdmissionRecords(admissionRecords.filter((_, i) => i !== index));
  };

  const updateAdmissionRecord = (index, field, value) => {
    setAdmissionRecords((prev) => {
      const next = [...prev];
      next[index] = { ...prev[index], [field]: value };
      return next;
    });
  };

  const validateCustomer = () => {
    const errors = [];
    if (!firstname || firstname.trim() === '') {
      errors.push('First Name');
    }
    if (!lastname || lastname.trim() === '') {
      errors.push('Last Name');
    }
    if (!customerType || customerType.trim() === '') {
      errors.push('Customer Type');
    }
    if (!programType || programType.trim() === '') {
      errors.push('Program Type');
    }
    if (!paySource || paySource.trim() === '') {
      errors.push('Pay Source');
    }
    if (paySource === 'other' && (!paySourceOther || paySourceOther.trim() === '')) {
      errors.push('Pay Source (Other)');
    }
    if (!legalSex || legalSex.trim() === '') {
      errors.push('Legal Sex');
    }
    if (!phone || phone.trim() === '') {
      errors.push('Primary Phone Number');
    }
    const primaryAddress = getCustomerAddressBySlot(addresses, 1);
    if (!primaryAddress?.line1 || primaryAddress.line1.trim() === '') {
      errors.push('Address Line 1');
    }
    if (!primaryAddress?.city || primaryAddress.city.trim() === '') {
      errors.push('City');
    }
    if (!primaryAddress?.state || primaryAddress.state.trim() === '') {
      errors.push('State');
    }
    if (!primaryAddress?.zipCode || primaryAddress.zipCode.trim() === '') {
      errors.push('Zip Code');
    }
    if (!emergencyContacts[0]?.name || emergencyContacts[0].name.trim() === '') {
      errors.push('Emergency Contact Name');
    }
    if (!emergencyContacts[0]?.phone || emergencyContacts[0].phone.trim() === '') {
      errors.push('Emergency Contact Phone Number');
    }
    
    if (errors.length > 0) {
      window.alert(`Please fill in the following required fields:\n${errors.join('\n')}`);
      return false;
    }
    return true;
  };

  const saveCustomer = () => {
    if (!validateCustomer()) {
      return;
    }
    
    const data = {
      // Basic Info
      name: `${lastname},${firstname}`,
      firstname,
      middle_name: middleName,
      lastname,
      name_cn: nameCN,
      name_on_id: nameOnId,
      type: customerType,
      program_type: programType,
      pay_source: paySource,
      pay_source_other: paySourceOther,
      birth_date: formatDateForBackend(birthDate),
      legal_sex: legalSex,
      gender: legalSex,
      marital_status: maritalStatus,
      marriage_date: formatDateForBackend(marriageDate),
      spouse_name: spouseName,
      spouse_customer_id: spouseCustomerId || '',
      immigration_status: immigrationStatus,
      immigration_status_other: immigrationStatusOther,
      language_spoken: languageSpoken.map(l => l.value),
      language_spoken_other: languageSpokenOther,
      language: languageSpoken.map(l => l.label).join(', '),
      
      // Contact Info
      phone,
      mobile_phone: phone2,
      home_phone: phone3,
      email,
      
      // Address slots 1–5 (stable; cleared slots stay empty in DB)
      ...buildCustomerAddressSaveFields(addresses),
      
      // Emergency Contact 1
      emergency_contact_name: emergencyContacts[0]?.name || '',
      emergency_contact_phone: emergencyContacts[0]?.phone || '',
      emergency_contact_relationship: emergencyContacts[0]?.relationship || '',
      emergency_contact_relationship_other: emergencyContacts[0]?.relationshipOther || '',
      emergency_contact_role: (emergencyContacts[0]?.role || []).map(r => r.value),
      emergency_contact: `${emergencyContacts[0]?.name || ''} ${emergencyContacts[0]?.phone || ''} ${emergencyContacts[0]?.relationship || ''}`,
      
      // Emergency Contact 2
      emergency_contact2_name: emergencyContacts[1]?.name || '',
      emergency_contact2_phone: emergencyContacts[1]?.phone || '',
      emergency_contact2_relationship: emergencyContacts[1]?.relationship || '',
      emergency_contact2_relationship_other: emergencyContacts[1]?.relationshipOther || '',
      emergency_contact2_role: (emergencyContacts[1]?.role || []).map(r => r.value),
      emergency_contact2: `${emergencyContacts[1]?.name || ''} ${emergencyContacts[1]?.phone || ''} ${emergencyContacts[1]?.relationship || ''}`,
      
      // Emergency Contact 3
      emergency_contact3_name: emergencyContacts[2]?.name || '',
      emergency_contact3_phone: emergencyContacts[2]?.phone || '',
      emergency_contact3_relationship: emergencyContacts[2]?.relationship || '',
      emergency_contact3_relationship_other: emergencyContacts[2]?.relationshipOther || '',
      emergency_contact3_role: (emergencyContacts[2]?.role || []).map(r => r.value),
      
      // Emergency Contact 4
      emergency_contact4_name: emergencyContacts[3]?.name || '',
      emergency_contact4_phone: emergencyContacts[3]?.phone || '',
      emergency_contact4_relationship: emergencyContacts[3]?.relationship || '',
      emergency_contact4_relationship_other: emergencyContacts[3]?.relationshipOther || '',
      emergency_contact4_role: (emergencyContacts[3]?.role || []).map(r => r.value),
      
      // Emergency Contact 5
      emergency_contact5_name: emergencyContacts[4]?.name || '',
      emergency_contact5_phone: emergencyContacts[4]?.phone || '',
      emergency_contact5_relationship: emergencyContacts[4]?.relationship || '',
      emergency_contact5_relationship_other: emergencyContacts[4]?.relationshipOther || '',
      emergency_contact5_role: (emergencyContacts[4]?.role || []).map(r => r.value),
      
      // Schedule
      days_of_week: daysOfWeek.map(d => d.value),
      
      // Admission & Discharge Record
      admission_records: sanitizeAdmissionRecordsForApi(admissionRecords, formatDateForBackend),
      ...legacyAdmissionFieldsFromFirstRecord(admissionRecords, formatDateForBackend),
      create_by: localStorage.getItem('user') && JSON.parse(localStorage.getItem('user'))?.name || '',
      discharge_date: formatDateForBackend(dischargeDate),
      discharge_reason: dischargeReason,
      discharge_reason_other: dischargeReason === 'other' ? dischargeReasonOther : '',
      
      // Care & Services
      dietary_restrictions: dietaryRestrictions.map(d => d.value),
      dietary_restrictions_other: dietaryRestrictionsOther,
      diet_texture: dietTexture,
      meal_type: mealType,
      food_allergies: foodAllergies,
      table_id: tableId,
      seat_number: seatNumber,
      transportation_type: transportationType,
      consent_to_text_messages: consentToTextMessages,
      text_msg_enabled: consentToTextMessages === 'yes',
      preferred_text_language: preferredTextLanguage,
      consent_to_media_use: consentToMediaUse,
      
      // Medical & Insurance - Providers
      primary_care_physician: primaryCarePhysician?.label || '',
      primary_care_physician_id: primaryCarePhysician?.value || '',
      care_provider: primaryCarePhysician?.label || '',
      pharmacy: pharmacy?.label || '',
      pharmacy_id: pharmacyId,
      
      // General Conditions
      diabetes_mellitus: diabetesMellitus,
      eyes_on: eyesOn,
      eyes_on_reason: eyesOnReason,
      has_airtag: hasAirtag,
      weight,
      disability: eyesOn === 'yes',
      wheelchair,
      
      // Legal
      molst,
      provisions_for_advance_medical: provisionsForAdvanceMedical,
      hospice,
      burial_arrangements: burialArrangements,
      power_of_attorney: powerOfAttorney,
      adcaps_note: adcapsNote,

      // Medication Management
      medications_given_at_center: medicationsGivenAtCenter,
      pillbox,
      medication_note: medicationNote,
      medication_type: medicationType,
      pillbox_type: pillboxType,
      pillbox_note: pillboxNote,
      
      // Rounding
      requires_rounding: requiresRounding,
      rounding_notes: roundingNotes,
      
      // Confidential Details
      medicare_number: medicareNumber,
      medicaid_number: medicaidNumber,
      social_security_number: socialSecurityNumber,
      ...socialBenefits,
      ltss_id_number: ltssIdNumber,
      
      // Compliance & Deadlines
      adcaps_completed_date: formatDateForBackend(adcapsCompletedDate),
      medicaid_expiration_date: formatDateForBackend(medicaidExpirationDate),
      va_expiration_date: formatDateForBackend(vaExpirationDate),
      medicaid_renewal: medicaidRenewal,
      medicaid_renew_date: formatDateForBackend(medicaidRenewDate),
      id_expiration_date: formatDateForBackend(idExpirationDate),
      
      // Additional Information
      note,
      edit_by: localStorage.getItem('user') && JSON.parse(localStorage.getItem('user'))?.name || '',
      status: 'active',

      // Form Submission flags — only include when a file was selected
      ...(hipaaAuthorizationForm ? { hipaa_authorization_form: 'true' } : {}),
      ...(medicationManagementConsentForm ? { medication_management_consent_form: 'true' } : {}),
      ...(freedomOfChoiceForm ? { freedom_of_choice_form: 'true' } : {}),
      ...(mealBenefitApplicationForm ? { meal_benefit_application_form: 'true' } : {}),
      ...(photoVideoReleaseForm ? { photo_video_release_form: 'true' } : {}),
      ...(securityDepositAgreementForm ? { security_deposit_agreement_form: 'true' } : {}),
      ...(recreationalProgramContractForm ? { recreational_program_contract_form: 'true' } : {}),
      ...(tbForm ? { tb_form: 'true' } : {}),
      ...(preScreeningForm ? { pre_screening_form: 'true' } : {}),
      tb_screening_yes_no: tbScreeningYesNo,
      pre_screening_yes_no: preScreeningYesNo,
      forty_five_day_admission_note: fortyFiveDayAdmissionNote,
      tb_notes: tbNotes,
    };
    
    const formData = new FormData();
    formData.append("file", selectedFile);
    let payload = { data };
    if (selectedFile) {
      payload = Object.assign({}, payload, {avatar: formData})
    }

    // Collect form files for upload after customer creation
    const formFiles = [
      { file: hipaaAuthorizationForm, fileType: 'hipaa_authorization_form' },
      { file: medicationManagementConsentForm, fileType: 'medication_management_consent_form' },
      { file: freedomOfChoiceForm, fileType: 'freedom_of_choice_form' },
      { file: mealBenefitApplicationForm, fileType: 'meal_benefit_application_form' },
      { file: photoVideoReleaseForm, fileType: 'photo_video_release_form' },
      { file: securityDepositAgreementForm, fileType: 'security_deposit_agreement_form' },
      { file: recreationalProgramContractForm, fileType: 'recreational_program_contract_form' },
      { file: tbForm, fileType: 'tb_form' },
      { file: preScreeningForm, fileType: 'pre_screening_form' },
    ].filter(f => f.file);

    if (formFiles.length > 0) {
      payload = Object.assign({}, payload, { formFiles });
    }

    CustomerService.getAllCustomers()
      .then((res) => {
        const customers = res?.data || [];
        const dup = findDuplicateCustomer(customers, { firstname, lastname, phone });
        if (dup) {
          pendingCustomerPayloadRef.current = payload;
          setDuplicateCustomerModalOpen(true);
          return;
        }
        dispatch(createCustomer(payload));
        resetBaseline();
        redirectAfterSave();
      })
      .catch(() => {
        dispatch(createCustomer(payload));
        resetBaseline();
        redirectAfterSave();
      });
  };

  // Custom styles for react-select — match native select height/border; fill equal-width columns
  const selectStyles = {
    container: (baseStyles) => ({
      ...baseStyles,
      width: '100%',
      minWidth: 0,
    }),
    control: (baseStyles) => ({
      ...baseStyles,
      minWidth: 0,
      width: '100%',
      minHeight: '45px',
      height: '45px',
      borderRadius: '8px',
      borderColor: '#ccc',
      boxShadow: 'none',
      '&:hover': {
        borderColor: '#ccc',
      },
    }),
    indicatorSeparator: () => ({
      display: 'none'
    }),
    dropdownIndicator: (baseStyles) => ({
      ...baseStyles,
      paddingRight: '8px',
      color: '#333',
      '&:hover': {
        color: '#000',
      },
    }),
    valueContainer: (baseStyles) => ({
      ...baseStyles,
      height: '43px',
      padding: '0 8px',
    }),
    input: (baseStyles) => ({
      ...baseStyles,
      margin: '0px',
      padding: '0px',
      height: '30px',
      width: 'auto',
    }),
    singleValue: (baseStyles) => ({
      ...baseStyles,
      margin: '0px',
    }),
    container: (baseStyles) => ({
      ...baseStyles,
      width: '100%',
    }),
  };

  const spouseSelectValue = spouseCustomerId
    ? spouseCustomerOptions.find((o) => o.value === spouseCustomerId) || null
    : null;

  const multiSelectStyles = {
    control: (baseStyles) => ({
      ...baseStyles,
      minWidth: '300px',
      minHeight: '45px',
      borderRadius: '8px'
    }),
    indicatorSeparator: () => ({
      display: 'none'
    }),
    dropdownIndicator: (baseStyles) => ({
      ...baseStyles,
      paddingRight: '8px',
      color: '#333',
      '&:hover': {
        color: '#000',
      },
    }),
    valueContainer: (baseStyles) => ({
      ...baseStyles,
      padding: '4px 8px',
    }),
    input: (baseStyles) => ({
      ...baseStyles,
      margin: '0px',
      padding: '0px',
      height: '30px',
      width: '290px'
    }),
  };

  return (
    <>
      <div className="list row mb-4">
        <Breadcrumb>
          <Breadcrumb.Item href="/">General</Breadcrumb.Item>
          <Breadcrumb.Item href="/customers/list">
            Customer Information
          </Breadcrumb.Item>
          <Breadcrumb.Item active>
            Create New Customer
          </Breadcrumb.Item>
        </Breadcrumb>
        <div className="col-md-12 text-primary">
           <h4>Create New Customer <button className="btn btn-link btn-sm" onClick={() => {goBack()}}>Back</button></h4> 
        </div>
      </div>

      <div className="app-main-content-list-container form-page">
        <div className="app-main-content-list-func-container">
          <Tabs defaultActiveKey="personalInfo" id="customers-tab">
            <Tab eventKey="personalInfo" title="Personal Information">
              {/* Basic Info Section — 5 columns per row (pic 2 order) */}
              <h6 className="text-primary">Basic Info</h6>
              <div className="customer-info-equal-row cols-5">
                <div className="customer-info-field">
                  <div className="field-label">First Name <span className="required">*</span></div>
                  <input type="text" placeholder="e.g., Alice" value={firstname} onChange={e => setFirstname(e.target.value)}/>
                </div>
                <div className="customer-info-field">
                  <div className="field-label">Middle Name</div>
                  <input type="text" placeholder="e.g., M." value={middleName} onChange={e => setMiddleName(e.target.value)}/>
                </div>
                <div className="customer-info-field">
                  <div className="field-label">Last Name <span className="required">*</span></div>
                  <input type="text" placeholder="e.g., Morgan" value={lastname} onChange={e => setLastname(e.target.value)}/>
                </div>
                <div className="customer-info-field">
                  <div className="field-label">Chinese / Preferred Name</div>
                  <input type="text" placeholder="e.g., 李华" value={nameCN} onChange={e => setNameCN(e.target.value)}/>
                </div>
                <div className="customer-info-field">
                  <div className="field-label">Name On ID</div>
                  <input type="text" placeholder="e.g., A. Morgan" value={nameOnId} onChange={e => setNameOnId(e.target.value)}/>
                </div>
              </div>
              <div className="customer-info-equal-row cols-5">
                <div className="customer-info-field">
                  <div className="field-label">Customer Type <span className="required">*</span></div>
                  <select value={customerType} onChange={e => setCustomerType(e.target.value)}>
                    <option value="">Select...</option>
                    {selectableCustomerTypes.map((typeOption) => (
                      <option key={typeOption} value={typeOption}>{CUSTOMER_TYPE_TEXT[typeOption] || typeOption}</option>
                    ))}
                  </select>
                </div>
                <div className="customer-info-field">
                  <div className="field-label">Legal Sex <span className="required">*</span></div>
                  <select value={legalSex} onChange={e => setLegalSex(e.target.value)}>
                    <option value="">Select...</option>
                    {Object.keys(LEGAL_SEX).map(key => (
                      <option key={key} value={LEGAL_SEX[key]}>{LEGAL_SEX_TEXT[LEGAL_SEX[key]]}</option>
                    ))}
                  </select>
                </div>
                <div className="customer-info-field">
                  <div className="field-label">Date Of Birth</div>
                  <DatePicker
                    selected={birthDate}
                    onChange={(date) => setBirthDate(date)}
                    dateFormat="MM/dd/yyyy"
                    placeholderText="e.g., 01/15/1950"
                    className="form-control"
                    showYearDropdown
                    scrollableYearDropdown
                    yearDropdownItemNumber={100}
                  />
                </div>
                <div className="customer-info-field">
                  <div className="field-label">Marriage Date</div>
                  <DatePicker
                    selected={marriageDate}
                    onChange={(date) => setMarriageDate(date)}
                    dateFormat="MM/dd/yyyy"
                    placeholderText="e.g., 06/12/1975"
                    className="form-control"
                    showYearDropdown
                    scrollableYearDropdown
                    yearDropdownItemNumber={100}
                  />
                </div>
                <div className="customer-info-field">
                  <div className="field-label">Language Spoken</div>
                  <MultiSelectDropdown
                    value={languageSpoken}
                    onChange={setLanguageSpoken}
                    options={LANGUAGE_OPTIONS}
                    placeholder="e.g., English, Mandarin"
                  />
                </div>
              </div>
              <div className="customer-info-equal-row cols-5">
                <div className="customer-info-field">
                  <div className="field-label">Program Type <span className="required">*</span></div>
                  <select value={programType} onChange={e => setProgramType(e.target.value)}>
                    <option value="">Select...</option>
                    {Object.keys(PROGRAM_TYPE).map(key => (
                      <option key={key} value={PROGRAM_TYPE[key]}>{PROGRAM_TYPE_TEXT[PROGRAM_TYPE[key]]}</option>
                    ))}
                  </select>
                </div>
                <div className="customer-info-field">
                  <div className="field-label">Pay Source <span className="required">*</span></div>
                  <select value={paySource} onChange={e => setPaySource(e.target.value)}>
                    <option value="">Select...</option>
                    {Object.keys(PAY_SOURCE).map(key => (
                      <option key={key} value={PAY_SOURCE[key]}>{PAY_SOURCE_TEXT[PAY_SOURCE[key]]}</option>
                    ))}
                  </select>
                </div>
                <div className="customer-info-field">
                  <div className="field-label">Marital Status</div>
                  <select value={maritalStatus} onChange={e => setMaritalStatus(e.target.value)}>
                    <option value="">Select...</option>
                    {Object.keys(MARITAL_STATUS).map(key => (
                      <option key={key} value={MARITAL_STATUS[key]}>{MARITAL_STATUS_TEXT[MARITAL_STATUS[key]]}</option>
                    ))}
                  </select>
                </div>
                <div className="customer-info-field">
                  <div className="field-label">Spouse Name</div>
                  <input
                    type="text"
                    placeholder="e.g., John Doe"
                    value={spouseName}
                    onChange={handleSpouseNameChange}
                  />
                </div>
                <div className="customer-info-field">
                  <div className="field-label">Immigration Status</div>
                  <select value={immigrationStatus} onChange={e => setImmigrationStatus(e.target.value)}>
                    <option value="">Select...</option>
                    {Object.keys(IMMIGRATION_STATUS).map(key => (
                      <option key={key} value={IMMIGRATION_STATUS[key]}>{IMMIGRATION_STATUS_TEXT[IMMIGRATION_STATUS[key]]}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="customer-info-equal-row cols-5">
                <div className="customer-info-field">
                  <div className="field-label">Spouse</div>
                  <Select
                    isClearable
                    isSearchable
                    options={spouseCustomerOptions}
                    value={spouseSelectValue}
                    onChange={handleSpouseCustomerSelect}
                    styles={selectStyles}
                    placeholder="Search active customers..."
                    noOptionsMessage={() => 'No active customers found'}
                  />
                </div>
              </div>
              {paySource === 'other' && (
                <div className="customer-info-equal-row cols-5">
                  <div className="customer-info-field">
                    <div className="field-label">Pay Source (Other) <span className="required">*</span></div>
                    <input type="text" placeholder="Please specify..." value={paySourceOther} onChange={e => setPaySourceOther(e.target.value)}/>
                  </div>
                </div>
              )}
              {(immigrationStatus === 'other' || languageSpoken.some(l => l.value === 'other')) && (
                <div className="customer-info-equal-row cols-5">
                  {immigrationStatus === 'other' && (
                    <div className="customer-info-field">
                      <div className="field-label">Immigration Status (Other)</div>
                      <input type="text" placeholder="Please specify..." value={immigrationStatusOther} onChange={e => setImmigrationStatusOther(e.target.value)}/>
                    </div>
                  )}
                  {languageSpoken.some(l => l.value === 'other') && (
                    <div className="customer-info-field">
                      <div className="field-label">Language Spoken (Other)</div>
                      <input type="text" placeholder="Please specify..." value={languageSpokenOther} onChange={e => setLanguageSpokenOther(e.target.value)}/>
                    </div>
                  )}
                </div>
              )}

              {/* Contact Info Section — 4 columns */}
              <h6 className="text-primary">Contact Info</h6>
              <div className="customer-info-equal-row cols-4">
                <div className="customer-info-field">
                  <div className="field-label">Primary Phone Number <span className="required">*</span></div>
                  <input type="text" placeholder="e.g., (240)463-1111" value={phone} onChange={e => setPhone(formatPhoneNumber(e.target.value))} maxLength={14}/>
                </div>
                <div className="customer-info-field">
                  <div className="field-label">Phone Number 2</div>
                  <input type="text" placeholder="e.g., (240)463-2222" value={phone2} onChange={e => setPhone2(formatPhoneNumber(e.target.value))} maxLength={14}/>
                </div>
                <div className="customer-info-field">
                  <div className="field-label">Phone Number 3</div>
                  <input type="text" placeholder="e.g., (240)463-3333" value={phone3} onChange={e => setPhone3(formatPhoneNumber(e.target.value))} maxLength={14}/>
                </div>
                <div className="customer-info-field">
                  <div className="field-label">Email</div>
                  <input type="email" placeholder="e.g., example@gmail.com" value={email} onChange={e => setEmail(e.target.value)}/>
                </div>
              </div>

              {/* Address Section — stable slots 1–5 (gaps stay empty) */}
              <h6 className="text-primary">Address</h6>
              {addresses.map((address, index) => (
                <div key={address.slot} className="address-block mb-3" style={{ borderBottom: index < addresses.length - 1 ? '1px solid #eee' : 'none', paddingBottom: '10px' }}>
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <strong className="text-secondary">Address {address.slot}</strong>
                    {address.slot !== 1 && (
                      <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => removeAddress(address.slot)}>Remove</button>
                    )}
                  </div>
                  <div className="customer-info-equal-row cols-6">
                    <div className="customer-info-field">
                      <div className="field-label">Address Line 1 {address.slot === 1 && <span className="required">*</span>}</div>
                      <input
                        type="text"
                        placeholder="e.g., 100 Sunshine Lane"
                        value={address.line1}
                        onChange={e => updateAddress(address.slot, 'line1', e.target.value)}
                      />
                    </div>
                    <div className="customer-info-field">
                      <div className="field-label">Address Line 2</div>
                      <input type="text" placeholder="e.g., Apt B2" value={address.line2} onChange={e => updateAddress(address.slot, 'line2', e.target.value)}/>
                    </div>
                    <div className="customer-info-field">
                      <div className="field-label">City {address.slot === 1 && <span className="required">*</span>}</div>
                      <input type="text" placeholder="e.g., Springfield" value={address.city} onChange={e => updateAddress(address.slot, 'city', e.target.value)}/>
                    </div>
                    <div className="customer-info-field">
                      <div className="field-label">State {address.slot === 1 && <span className="required">*</span>}</div>
                      <select value={address.state} onChange={e => updateAddress(address.slot, 'state', e.target.value)}>
                        <option value="">Select...</option>
                        {Object.keys(STATE_OPTIONS).map(key => (
                          <option key={key} value={STATE_OPTIONS[key]}>{STATE_OPTIONS_TEXT[STATE_OPTIONS[key]]}</option>
                        ))}
                        {address.state &&
                          !Object.values(STATE_OPTIONS).includes(address.state) && (
                            <option value={address.state}>{STATE_OPTIONS_TEXT[address.state] || address.state}</option>
                          )}
                      </select>
                    </div>
                    <div className="customer-info-field">
                      <div className="field-label">Zip Code {address.slot === 1 && <span className="required">*</span>}</div>
                      <input 
                        type="text" 
                        placeholder="e.g., 22000" 
                        value={address.zipCode} 
                        maxLength={5}
                        onChange={e => {
                          const value = e.target.value.replace(/[^0-9]/g, '');
                          updateAddress(address.slot, 'zipCode', value);
                        }}
                      />
                    </div>
                    <div className="customer-info-field">
                      <div className="field-label">Address Note</div>
                      <input type="text" placeholder="e.g., Senior Home" value={address.note} onChange={e => updateAddress(address.slot, 'note', e.target.value)}/>
                    </div>
                  </div>
                </div>
              ))}
              {addresses.length < MAX_CUSTOMER_ADDRESS_SLOTS && (
                <div className="mb-3">
                  <button type="button" className="btn btn-sm btn-outline-primary" onClick={addAddress}>+ Add Another Address</button>
                </div>
              )}

              {/* Emergency Contact Section — 4 columns */}
              <h6 className="text-primary">Emergency Contact</h6>
              {emergencyContacts.map((contact, index) => (
                <div key={index} className="emergency-contact-block mb-3" style={{ borderBottom: index < emergencyContacts.length - 1 ? '1px solid #eee' : 'none', paddingBottom: '10px' }}>
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <strong className="text-secondary">Emergency Contact {index + 1}</strong>
                    {emergencyContacts.length > 1 && (
                      <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => removeEmergencyContact(index)}>Remove</button>
                    )}
                  </div>
                  <div className="customer-info-equal-row cols-4">
                    <div className="customer-info-field">
                      <div className="field-label">Emergency Contact Name {index === 0 && <span className="required">*</span>}</div>
                      <input type="text" placeholder="e.g., Jordan Lee" value={contact.name} onChange={e => updateEmergencyContact(index, 'name', e.target.value)}/>
                    </div>
                    <div className="customer-info-field">
                      <div className="field-label">Emergency Contact Phone Number {index === 0 && <span className="required">*</span>}</div>
                      <input type="text" placeholder="e.g., (555)987-6543" value={contact.phone} onChange={e => updateEmergencyContact(index, 'phone', formatPhoneNumber(e.target.value))} maxLength={14}/>
                    </div>
                    <div className="customer-info-field">
                      <div className="field-label">Emergency Contact Relationship</div>
                      <select value={contact.relationship} onChange={e => updateEmergencyContact(index, 'relationship', e.target.value)}>
                        <option value="">Select...</option>
                        {Object.keys(EMERGENCY_CONTACT_RELATIONSHIP).map(key => (
                          <option key={key} value={EMERGENCY_CONTACT_RELATIONSHIP[key]}>{EMERGENCY_CONTACT_RELATIONSHIP_TEXT[EMERGENCY_CONTACT_RELATIONSHIP[key]]}</option>
                        ))}
                      </select>
                    </div>
                    <div className="customer-info-field">
                      <div className="field-label">Emergency Contact Role</div>
                      <MultiSelectDropdown
                        value={contact.role}
                        onChange={(value) => updateEmergencyContact(index, 'role', value)}
                        options={EMERGENCY_CONTACT_ROLE_OPTIONS}
                        placeholder="e.g., Power of Attorney"
                      />
                    </div>
                  </div>
                  {contact.relationship === 'other' && (
                    <div className="customer-info-equal-row cols-4">
                      <div className="customer-info-field">
                        <div className="field-label">Relationship (Other)</div>
                        <input type="text" placeholder="Please specify..." value={contact.relationshipOther} onChange={e => updateEmergencyContact(index, 'relationshipOther', e.target.value)}/>
                      </div>
                    </div>
                  )}
                </div>
              ))}
              {emergencyContacts.length < 5 && (
                <div className="mb-3">
                  <button type="button" className="btn btn-sm btn-outline-primary" onClick={addEmergencyContact}>+ Add Another Emergency Contact</button>
                </div>
              )}

              {/* Schedule Section */}
              <h6 className="text-primary">Schedule</h6>
              <div className="app-main-content-fields-section">
                <div className="me-4">
                  <div className="field-label">Days of Week</div>
                  <MultiSelectDropdown
                    value={daysOfWeek}
                    onChange={setDaysOfWeek}
                    options={DAYS_OF_WEEK_OPTIONS}
                    placeholder="e.g., Mon, Wed, Fri"
                  />
                </div>
              </div>

              {/* Admission & Discharge Record Section */}
              <h6 className="text-primary">Admission & Discharge Record</h6>
              {admissionRecords.map((rec, index) => (
                <div
                  key={index}
                  className="mb-3"
                  style={{
                    borderBottom: index < admissionRecords.length - 1 ? '1px solid #eee' : 'none',
                    paddingBottom: '10px',
                  }}
                >
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <strong className="text-secondary">
                      {index === 0 ? 'Current Admission' : `Admission ${index}`}
                    </strong>
                    {index > 0 && (
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => removeAdmissionRecord(index)}
                      >
                        Remove
                      </button>
                    )}
                  </div>
                  {index === 0 ? (
                    <div className="app-main-content-fields-section">
                      <div className="me-4">
                        <div className="field-label">Admission Date</div>
                        <DatePicker
                          selected={rec.admissionDate}
                          onChange={(date) => updateAdmissionRecord(index, 'admissionDate', date)}
                          dateFormat="MM/dd/yyyy"
                          placeholderText="e.g., 03/01/2024"
                          className="form-control"
                        />
                      </div>
                      <div className="me-4">
                        <div className="field-label">Anniversary Month</div>
                        <input
                          type="text"
                          readOnly
                          tabIndex={-1}
                          value={getAnniversaryMonthFromAdmissionDate(rec.admissionDate)}
                          placeholder="—"
                          className="form-control"
                          style={{ backgroundColor: '#e9ecef', cursor: 'default' }}
                        />
                      </div>
                      <div className="me-4">
                        <div className="field-label">Admission Number</div>
                        <input
                          type="text"
                          placeholder="e.g. 1"
                          value={rec.admissionNumber}
                          onChange={(e) => updateAdmissionRecord(index, 'admissionNumber', e.target.value)}
                        />
                      </div>
                      <div className="me-4" style={{ minWidth: '280px' }}>
                        <div className="field-label">Admitted From</div>
                        <Select
                          value={
                            rec.admittedFrom
                              ? { value: rec.admittedFrom, label: ADMITTED_FROM_TEXT[rec.admittedFrom] || rec.admittedFrom }
                              : null
                          }
                          onChange={(selected) => {
                            const v = selected?.value || '';
                            setAdmissionRecords((prev) => {
                              const next = [...prev];
                              next[index] = {
                                ...prev[index],
                                admittedFrom: v,
                                admittedFromOther: v === 'other' ? prev[index].admittedFromOther : '',
                              };
                              return next;
                            });
                          }}
                          options={withGroupedOrphanOption(
                            ADMITTED_FROM_GROUPED,
                            rec.admittedFrom,
                            ADMITTED_FROM_TEXT
                          )}
                          styles={selectStyles}
                          placeholder="Select..."
                          isClearable
                        />
                      </div>
                      {rec.admittedFrom === 'other' && (
                        <div className="me-4">
                          <div className="field-label">Admitted From (Other)</div>
                          <input
                            type="text"
                            placeholder="Please specify..."
                            value={rec.admittedFromOther}
                            onChange={(e) => updateAdmissionRecord(index, 'admittedFromOther', e.target.value)}
                          />
                        </div>
                      )}
                      <div className="me-4">
                        <div className="field-label">Enrolled Date</div>
                        <DatePicker
                          selected={rec.enrolledDate}
                          onChange={(date) => updateAdmissionRecord(index, 'enrolledDate', date)}
                          dateFormat="MM/dd/yyyy"
                          placeholderText="e.g., 03/02/2024"
                          className="form-control"
                        />
                      </div>
                      <div className="me-4">
                        <div className="field-label">Referral Source</div>
                        <Select
                          value={
                            rec.referralSource
                              ? {
                                  value: rec.referralSource,
                                  label: REFERRAL_SOURCE_TEXT[rec.referralSource] || rec.referralSource,
                                }
                              : null
                          }
                          onChange={(selected) => {
                            const v = selected?.value || '';
                            setAdmissionRecords((prev) => {
                              const next = [...prev];
                              next[index] = {
                                ...prev[index],
                                referralSource: v,
                                referralSourceOther: v === 'other' ? prev[index].referralSourceOther : '',
                              };
                              return next;
                            });
                          }}
                          options={withGroupedOrphanOption(
                            REFERRAL_SOURCE_GROUPED,
                            rec.referralSource,
                            REFERRAL_SOURCE_TEXT
                          )}
                          styles={selectStyles}
                          placeholder="e.g., Community Center"
                          isClearable
                        />
                      </div>
                      {rec.referralSource === 'other' && (
                        <div className="me-4">
                          <div className="field-label">Referral Source (Other)</div>
                          <input
                            type="text"
                            placeholder="Please specify..."
                            value={rec.referralSourceOther}
                            onChange={(e) => updateAdmissionRecord(index, 'referralSourceOther', e.target.value)}
                          />
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="app-main-content-fields-section">
                      <div className="me-4">
                        <div className="field-label">Admission Date</div>
                        <DatePicker
                          selected={rec.admissionDate}
                          onChange={(date) => updateAdmissionRecord(index, 'admissionDate', date)}
                          dateFormat="MM/dd/yyyy"
                          placeholderText="e.g., 03/01/2024"
                          className="form-control"
                        />
                      </div>
                      <div className="me-4" style={{ minWidth: '280px' }}>
                        <div className="field-label">Admitted From</div>
                        <Select
                          value={
                            rec.admittedFrom
                              ? { value: rec.admittedFrom, label: ADMITTED_FROM_TEXT[rec.admittedFrom] || rec.admittedFrom }
                              : null
                          }
                          onChange={(selected) => {
                            const v = selected?.value || '';
                            setAdmissionRecords((prev) => {
                              const next = [...prev];
                              next[index] = {
                                ...prev[index],
                                admittedFrom: v,
                                admittedFromOther: v === 'other' ? prev[index].admittedFromOther : '',
                              };
                              return next;
                            });
                          }}
                          options={withGroupedOrphanOption(
                            ADMITTED_FROM_GROUPED,
                            rec.admittedFrom,
                            ADMITTED_FROM_TEXT
                          )}
                          styles={selectStyles}
                          placeholder="Select..."
                          isClearable
                        />
                      </div>
                      {rec.admittedFrom === 'other' && (
                        <div className="me-4">
                          <div className="field-label">Admitted From (Other)</div>
                          <input
                            type="text"
                            placeholder="Please specify..."
                            value={rec.admittedFromOther}
                            onChange={(e) => updateAdmissionRecord(index, 'admittedFromOther', e.target.value)}
                          />
                        </div>
                      )}
                      <div className="me-4">
                        <div className="field-label">Discharge Date</div>
                        <DatePicker
                          selected={rec.dischargeDate}
                          onChange={(date) => updateAdmissionRecord(index, 'dischargeDate', date)}
                          dateFormat="MM/dd/yyyy"
                          placeholderText="MM/DD/YYYY"
                          className="form-control"
                        />
                      </div>
                      <div className="me-4" style={{ minWidth: '320px' }}>
                        <div className="field-label">Discharge Reason</div>
                        <Select
                          value={
                            rec.dischargeReason
                              ? {
                                  value: rec.dischargeReason,
                                  label: CUSTOMER_DISCHARGE_REASON_TEXT[rec.dischargeReason] || rec.dischargeReason,
                                }
                              : null
                          }
                          onChange={(selected) => {
                            const v = selected?.value || '';
                            setAdmissionRecords((prev) => {
                              const next = [...prev];
                              next[index] = {
                                ...prev[index],
                                dischargeReason: v,
                                dischargeReasonOther: v === 'other' ? prev[index].dischargeReasonOther : '',
                              };
                              return next;
                            });
                          }}
                          options={withGroupedOrphanOption(
                            CUSTOMER_DISCHARGE_REASON_GROUPED,
                            rec.dischargeReason,
                            CUSTOMER_DISCHARGE_REASON_TEXT
                          )}
                          styles={selectStyles}
                          placeholder="Select..."
                          isClearable
                        />
                      </div>
                      {rec.dischargeReason === 'other' && (
                        <div className="me-4">
                          <div className="field-label">Discharge Reason (Other)</div>
                          <input
                            type="text"
                            placeholder="Please specify..."
                            value={rec.dischargeReasonOther}
                            onChange={(e) => updateAdmissionRecord(index, 'dischargeReasonOther', e.target.value)}
                          />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
              {admissionRecords.length < MAX_ADMISSION_RECORDS && (
                <div className="mb-3">
                  <button type="button" className="btn btn-sm btn-outline-primary" onClick={addAdmissionRecord}>
                    Add another admission record
                  </button>
                </div>
              )}
              <div className="app-main-content-fields-section">
                <div className="me-4">
                  <div className="field-label">Discharge Date</div>
                  <DatePicker
                    selected={dischargeDate}
                    onChange={(date) => setDischargeDate(date)}
                    dateFormat="MM/dd/yyyy"
                    placeholderText="MM/DD/YYYY"
                    className="form-control"
                  />
                </div>
                <div className="me-4" style={{ minWidth: '320px' }}>
                  <div className="field-label">Discharge Reason</div>
                  <Select
                    value={
                      dischargeReason
                        ? {
                            value: dischargeReason,
                            label: CUSTOMER_DISCHARGE_REASON_TEXT[dischargeReason] || dischargeReason,
                          }
                        : null
                    }
                    onChange={(selected) => {
                      const v = selected?.value || '';
                      setDischargeReason(v);
                      if (v !== 'other') setDischargeReasonOther('');
                    }}
                    options={CUSTOMER_DISCHARGE_REASON_GROUPED}
                    styles={selectStyles}
                    placeholder="Select..."
                    isClearable
                  />
                </div>
                {dischargeReason === 'other' && (
                  <div className="me-4">
                    <div className="field-label">Discharge Reason (Other)</div>
                    <input type="text" placeholder="e.g., Transferred to Assisted Living" value={dischargeReasonOther} onChange={e => setDischargeReasonOther(e.target.value)}/>
                  </div>
                )}
              </div>

              <div className="list row mb-5">
                <div className="col-md-12 col-sm-12 col-xs-12">
                  <button className="btn btn-default btn-sm float-right" onClick={() => goBack()}> Cancel </button>
                  <button className="btn btn-primary btn-sm float-right" onClick={() => saveCustomer()}> Save </button>
                </div>
              </div>
            </Tab>

            <Tab eventKey="careServices" title="Care & Services">
              {/* Care & Services Section */}
              <h6 className="text-primary">Care & Services</h6>
              <div className="app-main-content-fields-section">
                <div className="me-4">
                  <div className="field-label">Food Allergies</div>
                  <input
                    type="text"
                    placeholder="e.g., Enter allergy and reaction"
                    value={foodAllergies}
                    onChange={(e) => setFoodAllergies(e.target.value)}
                  />
                </div>
                <div className="me-4">
                  <div className="field-label">Diet Preferences</div>
                  <MultiSelectDropdown
                    value={dietaryRestrictions}
                    onChange={setDietaryRestrictions}
                    options={DIETARY_RESTRICTIONS_GROUPED}
                    placeholder="e.g., No Pork"
                    exclusiveOptionValue="regular"
                  />
                </div>
                <div className="me-4">
                  <div className="field-label">Diet Texture</div>
                  <select value={dietTexture} onChange={e => setDietTexture(e.target.value)}>
                    <option value="">Select...</option>
                    {Object.keys(DIET_TEXTURE).map(key => (
                      <option key={key} value={DIET_TEXTURE[key]}>{DIET_TEXTURE_TEXT[DIET_TEXTURE[key]]}</option>
                    ))}
                  </select>
                </div>
                <div className="me-4">
                  <div className="field-label">Meal Type</div>
                  <select value={mealType} onChange={(e) => setMealType(e.target.value)}>
                    <option value="">Select...</option>
                    {Object.keys(MEAL_TYPE).map((key) => (
                      <option key={key} value={MEAL_TYPE[key]}>{MEAL_TYPE_TEXT[MEAL_TYPE[key]]}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="app-main-content-fields-section">
                <div className="me-4">
                  <div className="field-label">Table Number</div>
                  <input type="text" placeholder="e.g., T2" value={tableId} onChange={e => setTableId(e.target.value)} disabled/>
                  <small className="text-muted d-block">Auto-filled after choosing seat</small>
                </div>
                <div className="me-4">
                  <div className="field-label">Seat Number</div>
                  <input type="text" placeholder="e.g., B" value={seatNumber} onChange={e => setSeatNumber(e.target.value)} disabled/>
                  <small className="text-muted d-block">Auto-filled after choosing seat</small>
                </div>
                <div className="me-4">
                  <div className="field-label">Transportation Type</div>
                  <select value={transportationType} onChange={e => setTransportationType(e.target.value)}>
                    <option value="">Select...</option>
                    {Object.keys(TRANSPORTATION_TYPE).map(key => (
                      <option key={key} value={TRANSPORTATION_TYPE[key]}>{TRANSPORTATION_TYPE_TEXT[TRANSPORTATION_TYPE[key]]}</option>
                    ))}
                  </select>
                </div>
                <div className="me-4">
                  <div className="field-label">Lift-Equipped Vehicle Required</div>
                  <select value={wheelchair} onChange={e => setWheelchair(e.target.value)}>
                    <option value="">Select...</option>
                    {Object.keys(YES_NO).map(key => (
                      <option key={key} value={YES_NO[key]}>{YES_NO_TEXT[YES_NO[key]]}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="app-main-content-fields-section">
                <div className="me-4">
                  <div className="field-label">Consent To Text Messages</div>
                  <select value={consentToTextMessages} onChange={e => setConsentToTextMessages(e.target.value)}>
                    <option value="">Select...</option>
                    {Object.keys(YES_NO).map(key => (
                      <option key={key} value={YES_NO[key]}>{YES_NO_TEXT[YES_NO[key]]}</option>
                    ))}
                  </select>
                </div>
                <div className="me-4">
                  <div className="field-label">Preferred Text Language</div>
                  <select value={preferredTextLanguage} onChange={e => setPreferredTextLanguage(e.target.value)}>
                    <option value="">Select...</option>
                    {Object.keys(PREFERRED_TEXT_LANGUAGE).map(key => (
                      <option key={key} value={PREFERRED_TEXT_LANGUAGE[key]}>{PREFERRED_TEXT_LANGUAGE_TEXT[PREFERRED_TEXT_LANGUAGE[key]]}</option>
                    ))}
                  </select>
                </div>
                <div className="me-4">
                  <div className="field-label">Consent to Media Use</div>
                  <select value={consentToMediaUse} onChange={e => setConsentToMediaUse(e.target.value)}>
                    <option value="">Select...</option>
                    {Object.keys(YES_NO).map(key => (
                      <option key={key} value={YES_NO[key]}>{YES_NO_TEXT[YES_NO[key]]}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="list row mb-5">
                <div className="col-md-12 col-sm-12 col-xs-12">
                  <button className="btn btn-default btn-sm float-right" onClick={() => goBack()}> Cancel </button>
                  <button className="btn btn-primary btn-sm float-right" onClick={() => saveCustomer()}> Save </button>
                </div>
              </div>
            </Tab>

            <Tab eventKey="medicalInsurance" title="Medical & Insurance">
              {/* Providers Section */}
              <h6 className="text-primary">Providers</h6>
              <div className="customer-info-equal-row cols-4">
                <div className="customer-info-field">
                  <div className="field-label">Primary Care Physician</div>
                  <Select
                    value={primaryCarePhysician}
                    onChange={onPrimaryCarePhysicianChange}
                    options={[{value: '', label: ''}, ...pcpProviders.map(provider => ({
                      value: provider?.id || '',
                      label: provider?.name || '',
                    }))]}
                    styles={selectStyles}
                    placeholder="e.g., Dr. Smith"
                  />
                </div>
                <div className="customer-info-field">
                  <div className="field-label">Pharmacy Name</div>
                  <Select
                    value={pharmacy}
                    onChange={onPharmacyChange}
                    options={[{value: '', label: ''}, ...activePharmacies.map(pharm => ({
                      value: pharm?.id || '',
                      label: pharm?.name || '',
                    }))]}
                    styles={selectStyles}
                    placeholder="e.g., BrightCare Pharmacy"
                  />
                </div>
                <div className="customer-info-field">
                  <div className="field-label">Pharmacy ID</div>
                  <input type="text" placeholder="e.g., RX778899" value={pharmacyId} onChange={e => setPharmacyId(e.target.value)}/>
                </div>
              </div>

              {/* General Conditions Section */}
              <h6 className="text-primary">General Conditions</h6>
              <div className="customer-info-equal-row cols-4">
                <div className="customer-info-field span-2">
                  <div className="field-label">Diabetes Mellitus</div>
                  <select value={diabetesMellitus} onChange={e => setDiabetesMellitus(e.target.value)}>
                    <option value="">Select...</option>
                    {Object.keys(YES_NO).map(key => (
                      <option key={key} value={YES_NO[key]}>{YES_NO_TEXT[YES_NO[key]]}</option>
                    ))}
                  </select>
                </div>
                <div className="customer-info-field">
                  <div className="field-label">Eyes-On</div>
                  <select value={eyesOn} onChange={e => setEyesOn(e.target.value)}>
                    <option value="">Select...</option>
                    {Object.keys(YES_NO).map(key => (
                      <option key={key} value={YES_NO[key]}>{YES_NO_TEXT[YES_NO[key]]}</option>
                    ))}
                  </select>
                </div>
                <div className="customer-info-field">
                  <div className="field-label">Eyes-On Reason</div>
                  <input
                    type="text"
                    placeholder="e.g., Enter reason for eyes-on"
                    value={eyesOnReason}
                    onChange={(e) => setEyesOnReason(e.target.value)}
                  />
                </div>
                <div className="customer-info-field">
                  <div className="field-label">Has Airtag</div>
                  <select value={hasAirtag} onChange={(e) => setHasAirtag(e.target.value)}>
                    <option value="">Select...</option>
                    {Object.keys(YES_NO).map((key) => (
                      <option key={key} value={YES_NO[key]}>{YES_NO_TEXT[YES_NO[key]]}</option>
                    ))}
                  </select>
                </div>
                <div className="customer-info-field">
                  <div className="field-label">Weight</div>
                  <input
                    type="text"
                    placeholder="Enter weight (lb)"
                    value={weight}
                    onChange={(e) => setWeight(e.target.value)}
                  />
                </div>
              </div>

              <h6 className="text-primary">TB & Pre Screening</h6>
              <div className="app-main-content-fields-section">
                <div className="me-4">
                  <div className="field-label">TB Cleared</div>
                  <select value={tbScreeningYesNo} onChange={(e) => setTbScreeningYesNo(e.target.value)}>
                    <option value="">Select...</option>
                    {Object.keys(YES_NO).map((key) => (
                      <option key={key} value={YES_NO[key]}>{YES_NO_TEXT[YES_NO[key]]}</option>
                    ))}
                  </select>
                </div>
                <div className="me-4">
                  <div className="field-label">Pre-Screening Form</div>
                  <select value={preScreeningYesNo} onChange={(e) => setPreScreeningYesNo(e.target.value)}>
                    <option value="">Select...</option>
                    {Object.keys(PRE_SCREENING_FORM).map((key) => (
                      <option key={key} value={PRE_SCREENING_FORM[key]}>{PRE_SCREENING_FORM_TEXT[PRE_SCREENING_FORM[key]]}</option>
                    ))}
                  </select>
                </div>
                <div className="me-4">
                  <div className="field-label">45 Days Admission Note</div>
                  <select value={fortyFiveDayAdmissionNote} onChange={(e) => setFortyFiveDayAdmissionNote(e.target.value)}>
                    <option value="">Select...</option>
                    {Object.keys(YES_NO).map((key) => (
                      <option key={key} value={YES_NO[key]}>{YES_NO_TEXT[YES_NO[key]]}</option>
                    ))}
                  </select>
                </div>
                <div className="me-4">
                  <div className="field-label">TB Notes</div>
                  <input
                    type="text"
                    placeholder="e.g., Note about TB form"
                    value={tbNotes}
                    onChange={(e) => setTbNotes(e.target.value)}
                  />
                </div>
              </div>

              {/* Legal Section */}
              <h6 className="text-primary">Legal</h6>
              <div className="app-main-content-fields-section">
                <div className="me-4">
                  <div className="field-label">MOLST</div>
                  <input type="text" placeholder="e.g., Attempt CPR" value={molst} onChange={e => setMolst(e.target.value)}/>
                </div>
                <div className="me-4">
                  <div className="field-label">Advance Directive /Living Will</div>
                  <select value={provisionsForAdvanceMedical} onChange={e => setProvisionsForAdvanceMedical(e.target.value)}>
                    <option value="">Select...</option>
                    {Object.keys(YES_NO).map(key => (
                      <option key={key} value={YES_NO[key]}>{YES_NO_TEXT[YES_NO[key]]}</option>
                    ))}
                  </select>
                </div>
                <div className="me-4">
                  <div className="field-label">Hospice</div>
                  <select value={hospice} onChange={e => setHospice(e.target.value)}>
                    <option value="">Select...</option>
                    {Object.keys(YES_NO).map(key => (
                      <option key={key} value={YES_NO[key]}>{YES_NO_TEXT[YES_NO[key]]}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="app-main-content-fields-section">
                <div className="me-4">
                  <div className="field-label">CPR</div>
                  <select value={burialArrangements} onChange={e => setBurialArrangements(e.target.value)}>
                    <option value="">Select...</option>
                    {Object.keys(YES_NO).map(key => (
                      <option key={key} value={YES_NO[key]}>{YES_NO_TEXT[YES_NO[key]]}</option>
                    ))}
                  </select>
                </div>
                <div className="me-4">
                  <div className="field-label">Power of Attorney</div>
                  <input type="text" placeholder="e.g., Jone" value={powerOfAttorney} onChange={e => setPowerOfAttorney(e.target.value)}/>
                </div>
              </div>

              {/* Medication Management Section */}
              <h6 className="text-primary">Medication Management</h6>
              <div className="customer-info-equal-row cols-6">
                <div className="customer-info-field">
                  <div className="field-label">Medications given at the center</div>
                  <select value={medicationsGivenAtCenter} onChange={e => setMedicationsGivenAtCenter(e.target.value)}>
                    <option value="">Select...</option>
                    {Object.keys(YES_NO).map(key => (
                      <option key={key} value={YES_NO[key]}>{YES_NO_TEXT[YES_NO[key]]}</option>
                    ))}
                  </select>
                </div>
                <div className="customer-info-field">
                  <div className="field-label">Pillbox</div>
                  <select value={pillbox} onChange={e => setPillbox(e.target.value)}>
                    <option value="">Select...</option>
                    {Object.keys(YES_NO).map(key => (
                      <option key={key} value={YES_NO[key]}>{YES_NO_TEXT[YES_NO[key]]}</option>
                    ))}
                  </select>
                </div>
                <div className="customer-info-field">
                  <div className="field-label">Medication Note</div>
                  <input type="text" placeholder="e.g., Note about medication" value={medicationNote} onChange={e => setMedicationNote(e.target.value)}/>
                </div>
                <div className="customer-info-field">
                  <div className="field-label">Medication Type</div>
                  <input type="text" placeholder="Enter medication type" value={medicationType} onChange={e => setMedicationType(e.target.value)}/>
                </div>
                <div className="customer-info-field">
                  <div className="field-label">Pillbox Type</div>
                  <input type="text" placeholder="Enter pillbox type" value={pillboxType} onChange={e => setPillboxType(e.target.value)}/>
                </div>
                <div className="customer-info-field">
                  <div className="field-label">Pillbox Note</div>
                  <input type="text" placeholder="Enter pillbox note" value={pillboxNote} onChange={e => setPillboxNote(e.target.value)}/>
                </div>
              </div>

              {/* Rounding Section */}
              <h6 className="text-primary">Rounding</h6>
              <div className="app-main-content-fields-section">
                <div className="me-4">
                  <div className="field-label">Requires Rounding</div>
                  <select value={requiresRounding} onChange={e => setRequiresRounding(e.target.value)}>
                    <option value="">Select...</option>
                    {Object.keys(YES_NO).map(key => (
                      <option key={key} value={YES_NO[key]}>{YES_NO_TEXT[YES_NO[key]]}</option>
                    ))}
                  </select>
                </div>
                <div className="me-4">
                  <div className="field-label">Rounding Notes</div>
                  <input type="text" placeholder="e.g., Escorting" value={roundingNotes} onChange={e => setRoundingNotes(e.target.value)}/>
                </div>
              </div>

              <div className="list row mb-5">
                <div className="col-md-12 col-sm-12 col-xs-12">
                  <button className="btn btn-default btn-sm float-right" onClick={() => goBack()}> Cancel </button>
                  <button className="btn btn-primary btn-sm float-right" onClick={() => saveCustomer()}> Save </button>
                </div>
              </div>
            </Tab>

            <Tab eventKey="confidentialDetails" title="Confidential Details">
              <h6 className="text-primary">Social Benefits</h6>
              <div className="app-main-content-fields-section">
                {SOCIAL_BENEFIT_FIELD_KEYS.map(({ key, label }) => (
                  <div className="me-4" key={key}>
                    <div className="field-label">{label}</div>
                    <select
                      value={socialBenefits[key] || ''}
                      onChange={(e) =>
                        setSocialBenefits((prev) => ({ ...prev, [key]: e.target.value }))
                      }
                    >
                      <option value="">Select...</option>
                      {Object.keys(YES_NO).map((ynKey) => (
                        <option key={ynKey} value={YES_NO[ynKey]}>
                          {YES_NO_TEXT[YES_NO[ynKey]]}
                        </option>
                      ))}
                    </select>
                  </div>
                ))}
              </div>
              <h6 className="text-primary">Confidential Details</h6>
              <div className="app-main-content-fields-section">
                <div className="me-4">
                  <div className="field-label">Medicare Number</div>
                  <input type="text" placeholder="e.g., MC11223344" value={medicareNumber} onChange={e => setMedicareNumber(e.target.value)}/>
                </div>
                <div className="me-4">
                  <div className="field-label">Medicaid Number</div>
                  <input type="text" placeholder="e.g., MD99887766" value={medicaidNumber} onChange={e => setMedicaidNumber(e.target.value)}/>
                </div>
                <div className="me-4">
                  <div className="field-label">Social Security Number</div>
                  <input type="text" placeholder="e.g., 123-45-6789" value={socialSecurityNumber} onChange={e => setSocialSecurityNumber(e.target.value)}/>
                </div>
              </div>

              <div className="list row mb-5">
                <div className="col-md-12 col-sm-12 col-xs-12">
                  <button className="btn btn-default btn-sm float-right" onClick={() => goBack()}> Cancel </button>
                  <button className="btn btn-primary btn-sm float-right" onClick={() => saveCustomer()}> Save </button>
                </div>
              </div>
            </Tab>

            <Tab eventKey="complianceDeadlines" title="Compliance & Deadlines">
              <h6 className="text-primary">Compliance & Deadlines</h6>
              <div className="app-main-content-fields-section">
                <div className="me-4">
                  <div className="field-label">Adcaps Due Date</div>
                  <DatePicker
                    selected={adcapsCompletedDate}
                    onChange={(date) => setAdcapsCompletedDate(date)}
                    dateFormat="MM/dd/yyyy"
                    placeholderText="e.g., 06/15/2026"
                    className="form-control"
                  />
                </div>
                <div className="me-4">
                  <div className="field-label">Next ADCAPS Due Date</div>
                  <input
                    type="text"
                    readOnly
                    className="form-control"
                    style={{ background: '#f5f5f5' }}
                    placeholder="e.g., 12/31/2026"
                    value={formatDateUsMdY(computeNextAdcapsDueDate(adcapsCompletedDate))}
                  />
                  <small className="text-muted d-block">Adcaps Due Date + 120 days</small>
                </div>
                <div className="me-4">
                  <div className="field-label">ADCAPS Remaining Days</div>
                  <input
                    type="text"
                    readOnly
                    className="form-control"
                    style={{ background: '#f5f5f5' }}
                    placeholder="e.g., 30"
                    value={adcapsCompletedDate ? computeAdcapsRemainingDays(adcapsCompletedDate) : ''}
                  />
                  <small className="text-muted d-block">Days from today to next ADCAPS due</small>
                </div>
                <div className="me-4">
                  <div className="field-label">ADCAPS Note</div>
                  <input type="text" placeholder="e.g., Note about ADCAPS" value={adcapsNote} onChange={e => setAdcapsNote(e.target.value)}/>
                </div>
                <div className="me-4">
                  <div className="field-label">LTSS ID Number</div>
                  <input
                    type="text"
                    placeholder="e.g., 123456"
                    value={ltssIdNumber}
                    onChange={(e) => setLtssIdNumber(e.target.value)}
                  />
                </div>
                <div className="me-4">
                  <div className="field-label">Medicaid Expiration Date</div>
                  <DatePicker
                    selected={medicaidExpirationDate}
                    onChange={(date) => setMedicaidExpirationDate(date)}
                    dateFormat="MM/dd/yyyy"
                    placeholderText="e.g., 12/31/2026"
                    className="form-control"
                  />
                </div>
                <div className="me-4">
                  <div className="field-label">VA Expiration Date</div>
                  <DatePicker
                    selected={vaExpirationDate}
                    onChange={(date) => setVaExpirationDate(date)}
                    dateFormat="MM/dd/yyyy"
                    placeholderText="e.g., 12/31/2026"
                    className="form-control"
                  />
                </div>
                <div className="me-4">
                  <div className="field-label">Medicaid Renewal</div>
                  <select value={medicaidRenewal} onChange={(e) => setMedicaidRenewal(e.target.value)}>
                    <option value="">Select...</option>
                    {Object.keys(YES_NO).map((key) => (
                      <option key={key} value={YES_NO[key]}>{YES_NO_TEXT[YES_NO[key]]}</option>
                    ))}
                  </select>
                </div>
                <div className="me-4">
                  <div className="field-label">Medicaid Renew Date</div>
                  <DatePicker
                    selected={medicaidRenewDate}
                    onChange={(date) => setMedicaidRenewDate(date)}
                    dateFormat="MM/dd/yyyy"
                    placeholderText="e.g., 06/15/2026"
                    className="form-control"
                  />
                </div>
                <div className="me-4">
                  <div className="field-label">ID Expiration Date</div>
                  <DatePicker
                    selected={idExpirationDate}
                    onChange={(date) => setIdExpirationDate(date)}
                    dateFormat="MM/dd/yyyy"
                    placeholderText="e.g., 12/31/2026"
                    className="form-control"
                  />
                </div>
              </div>

              <div className="list row mb-5">
                <div className="col-md-12 col-sm-12 col-xs-12">
                  <button className="btn btn-default btn-sm float-right" onClick={() => goBack()}> Cancel </button>
                  <button className="btn btn-primary btn-sm float-right" onClick={() => saveCustomer()}> Save </button>
                </div>
              </div>
            </Tab>

            <Tab eventKey="formSubmission" title="Form Submission">
              {/* Admission Forms Section */}
              <h6 className="text-primary">Admission Forms</h6>
              <div className="app-main-content-fields-section base-line">
                <div className="me-4">
                  <div className="field-label">Maryland HIPAA Authorization</div>
                  <label className="custom-file-upload">
                    <Upload width={20} color={"#fff"} className="me-2"></Upload> Upload
                    <input type="file" onChange={(e) => setHipaaAuthorizationForm(e.target.files[0])}/>
                  </label>
                  <div className="file-name">{hipaaAuthorizationForm?.name}</div>
                </div>
                <div className="me-4">
                  <div className="field-label">Medication Management Consent Form</div>
                  <label className="custom-file-upload">
                    <Upload width={20} color={"#fff"} className="me-2"></Upload> Upload
                    <input type="file" onChange={(e) => setMedicationManagementConsentForm(e.target.files[0])}/>
                  </label>
                  <div className="file-name">{medicationManagementConsentForm?.name}</div>
                </div>
                <div className="me-4">
                  <div className="field-label">Freedom Of Choice (Medicaid only)</div>
                  <label className="custom-file-upload">
                    <Upload width={20} color={"#fff"} className="me-2"></Upload> Upload
                    <input type="file" onChange={(e) => setFreedomOfChoiceForm(e.target.files[0])}/>
                  </label>
                  <div className="file-name">{freedomOfChoiceForm?.name}</div>
                </div>
              </div>
              <div className="app-main-content-fields-section base-line">
                <div className="me-4">
                  <div className="field-label">Meal Benefit Application</div>
                  <label className="custom-file-upload">
                    <Upload width={20} color={"#fff"} className="me-2"></Upload> Upload
                    <input type="file" onChange={(e) => setMealBenefitApplicationForm(e.target.files[0])}/>
                  </label>
                  <div className="file-name">{mealBenefitApplicationForm?.name}</div>
                </div>
                <div className="me-4">
                  <div className="field-label">Photo-Video Release Waiver Form</div>
                  <label className="custom-file-upload">
                    <Upload width={20} color={"#fff"} className="me-2"></Upload> Upload
                    <input type="file" onChange={(e) => setPhotoVideoReleaseForm(e.target.files[0])}/>
                  </label>
                  <div className="file-name">{photoVideoReleaseForm?.name}</div>
                </div>
                <div className="me-4">
                  <div className="field-label">Security Deposit Payment Agreement (Self Pay only)</div>
                  <label className="custom-file-upload">
                    <Upload width={20} color={"#fff"} className="me-2"></Upload> Upload
                    <input type="file" onChange={(e) => setSecurityDepositAgreementForm(e.target.files[0])}/>
                  </label>
                  <div className="file-name">{securityDepositAgreementForm?.name}</div>
                </div>
              </div>
              <div className="app-main-content-fields-section base-line">
                <div className="me-4">
                  <div className="field-label">Recreational Program Contract (Senior Plus only)</div>
                  <label className="custom-file-upload">
                    <Upload width={20} color={"#fff"} className="me-2"></Upload> Upload
                    <input type="file" onChange={(e) => setRecreationalProgramContractForm(e.target.files[0])}/>
                  </label>
                  <div className="file-name">{recreationalProgramContractForm?.name}</div>
                </div>
              </div>

              {/* Medical Forms Section */}
              <h6 className="text-primary">Medical Forms</h6>
              <div className="app-main-content-fields-section base-line">
                <div className="me-4">
                  <div className="field-label">TB Form</div>
                  <label className="custom-file-upload">
                    <Upload width={20} color={"#fff"} className="me-2"></Upload> Upload
                    <input type="file" onChange={(e) => setTbForm(e.target.files[0])}/>
                  </label>
                  <div className="file-name">{tbForm?.name}</div>
                </div>
                <div className="me-4">
                  <div className="field-label">Pre-Screening Form</div>
                  <label className="custom-file-upload">
                    <Upload width={20} color={"#fff"} className="me-2"></Upload> Upload
                    <input type="file" onChange={(e) => setPreScreeningForm(e.target.files[0])}/>
                  </label>
                  <div className="file-name">{preScreeningForm?.name}</div>
                </div>
              </div>

              <div className="list row mb-5">
                <div className="col-md-12 col-sm-12 col-xs-12">
                  <button className="btn btn-default btn-sm float-right" onClick={() => goBack()}> Cancel </button>
                  <button className="btn btn-primary btn-sm float-right" onClick={() => saveCustomer()}> Save </button>
                </div>
              </div>
            </Tab>

            <Tab eventKey="additionalInfo" title="Additional Information">
              <h6 className="text-primary">Additional Information</h6>
              <div className="app-main-content-fields-section base-line">
                <div className="me-4">
                  <div className="field-label">Profile Picture</div>
                  <label className="custom-file-upload">
                    <Upload width={20} color={"#fff"} className="me-2"></Upload> Upload Files
                    <input type="file" onChange={(e) => setSelectedFile(e.target.files[0])}/>
                  </label>
                  <div className="file-name">{selectedFile?.name}</div>
                </div>
                <div className="me-4">
                  <div className="field-label">Notes And Attachments</div>
                  <textarea 
                    placeholder="e.g., Customer enjoys art activities" 
                    value={note} 
                    onChange={e => setNote(e.target.value)}
                    rows={4}
                    style={{width: '400px'}}
                  />
                </div>
              </div>

              <div className="list row mb-5">
                <div className="col-md-12 col-sm-12 col-xs-12">
                  <button className="btn btn-default btn-sm float-right" onClick={() => goBack()}> Cancel </button>
                  <button className="btn btn-primary btn-sm float-right" onClick={() => saveCustomer()}> Save </button>
                </div>
              </div>
            </Tab>
          </Tabs>
        </div>
      </div>

      <Modal
        show={duplicateCustomerModalOpen}
        onHide={() => {
          pendingCustomerPayloadRef.current = null;
          setDuplicateCustomerModalOpen(false);
        }}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Possible duplicate customer</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          A customer already exists with the same first name, last name, and phone number. Do you want to create another record anyway?
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              pendingCustomerPayloadRef.current = null;
              setDuplicateCustomerModalOpen(false);
            }}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              const payloadToSend = pendingCustomerPayloadRef.current;
              pendingCustomerPayloadRef.current = null;
              setDuplicateCustomerModalOpen(false);
              if (payloadToSend) {
                dispatch(createCustomer(payloadToSend));
                resetBaseline();
                redirectAfterSave();
              }
            }}
          >
            Confirm
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default CreateCustomer;
